import { Link } from 'react-router-dom'
import { usePvKostenRechnerSeo } from '../../seo/usePvKostenRechnerSeo'
import type { PvKostenRechnerFaqItem } from '../../seo/usePvKostenRechnerSeo'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { PvKostenRechnerWidget } from './PvKostenRechnerWidget'
import { usePriceCalculatorCta } from './usePriceCalculatorCta'
import { useMarketingHeaderActions } from './useMarketingHeaderActions'

const FAQ: PvKostenRechnerFaqItem[] = [
  {
    q: 'Wie berechnet Roofora den Durchschnittspreis?',
    a: 'Wir werten alle bei Roofora abgegebenen Handwerker-Angebote aus, deren Anlagengröße und Speicherausstattung ungefähr zu Ihrer Eingabe passen. Reichen die Angebote aus Ihrer Region nicht aus, weisen wir das Ergebnis transparent als bundesweiten Durchschnitt statt eines regionalen Wertes aus. Gibt es selbst bundesweit keine ähnlich großen Angebote, zeigen wir stattdessen eine grobe Schätzung aus dem Preis pro kWp/Speicher-kWh über alle bisherigen Komplettsystem-Angebote - deutlich als solche gekennzeichnet. Sie sehen immer, welche Basis gerade verwendet wurde.',
  },
  {
    q: 'Wie viel kostet eine Photovoltaikanlage pro kWp?',
    a: 'Das hängt stark von Anlagengröße, Speicherwunsch und Region ab - pauschale Angaben pro kWp sind deshalb wenig aussagekräftig. Geben Sie oben Ihre gewünschte Anlagengröße ein, um einen Durchschnittspreis inklusive Preis pro kWp aus echten Angeboten zu sehen.',
  },
  {
    q: 'Was kostet ein Batteriespeicher pro kWh?',
    a: 'Auch das lässt sich nicht seriös pauschalisieren - tragen Sie oben eine gewünschte Speichergröße ein (oder wählen Sie „Kein Batteriespeicher“), um einen Vergleichspreis aus echten Angeboten mit ähnlicher Speicherausstattung zu erhalten.',
  },
  {
    q: 'Muss ich meine Kontaktdaten angeben, um den Preis zu sehen?',
    a: 'Nein. Der Kostenrechner funktioniert komplett ohne Registrierung, E-Mail-Adresse oder Telefonnummer - anders als bei manchen anderen Anbietern, bei denen erst ein Kontaktformular ausgefüllt werden muss, um überhaupt einen Preis zu sehen.',
  },
  {
    q: 'Warum dauert die Berechnung nur ca. 2 Sekunden?',
    a: 'Weil keine Formulare, keine Bestätigungs-E-Mail und kein manueller Prüfschritt nötig sind - der Rechner vergleicht Ihre Eingabe direkt mit den bereits vorliegenden Handwerker-Angeboten in unserer Datenbank.',
  },
  {
    q: 'Woher stammen die Vergleichspreise?',
    a: 'Ausschließlich aus echten, bei Roofora abgegebenen Festpreis-Angeboten von Handwerksbetrieben - keine Formelschätzung und keine Herstellerpreislisten.',
  },
  {
    q: 'Wofür eignet sich der Durchschnittspreis?',
    a: 'Als schnelle Orientierung vorab - etwa für die eigene Budgetplanung oder um ein späteres Angebot besser einordnen zu können. Für ein verbindliches Festpreis-Angebot für Ihr konkretes Dach benötigen Sie weiterhin eine echte Anfrage bei Roofora.',
  },
]

/** Public, anonymous /pv-kostenrechner landing page (tasks/new-things/preis-durchschnittsrechner/
 *  05-seo-landingpages.md) - German-only bespoke route like /pv-rechner (see PvRechnerPage.tsx),
 *  not registered in content/landingPages (that generic system has no widget slot, see README).
 *  Embeds the same PvKostenRechnerWidget as the main landing page (03), plus more elaborate
 *  trust-building/FAQ text than there's room for on the homepage. */
export function PvKostenRechnerPage() {
  usePvKostenRechnerSeo(FAQ)
  const headerActions = useMarketingHeaderActions()
  const handleCalculatorRequestOffer = usePriceCalculatorCta(headerActions.onSelectCustomer)

  return (
    <>
      <MarketingHeader {...headerActions} />
      <article className="blog-page seo-landing-page">
        <p className="eyebrow">Kostenlos &amp; ohne Anmeldung</p>
        <h1>Photovoltaik-Durchschnittspreis berechnen</h1>
        <p className="pv-rechner-intro">
          Erhalten Sie kostenlos in weniger als 2 Sekunden den durchschnittlichen Preis Ihrer
          Photovoltaikanlage - basierend auf echten Angebotsdaten, ganz ohne Registrierung,
          Kontaktdaten oder Anruf.
        </p>

        <PvKostenRechnerWidget onRequestOffer={handleCalculatorRequestOffer} />

        <h2>Wie funktioniert der PV-Kostenrechner?</h2>
        <p>
          Anders als bei einer pauschalen Preistabelle vergleichen wir Ihre Eingabe - Postleitzahl,
          gewünschte Anlagengröße und Speicherwunsch - direkt mit echten, bei Roofora abgegebenen
          Festpreis-Angeboten von Handwerksbetrieben. Passen genug Angebote aus Ihrer Region, zeigen
          wir einen regionalen Durchschnitt; reicht die Datenbasis vor Ort nicht aus, weichen wir
          transparent gekennzeichnet auf einen bundesweiten Durchschnitt aus statt Ihnen eine
          scheinbar regionale Zahl vorzutäuschen. Je mehr Angebote über die Plattform eingehen, desto
          genauer werden auch die regionalen Durchschnittspreise.
        </p>

        <h2>Warum Roofora?</h2>
        <p>
          Die Berechnung ist komplett kostenlos, weil sie kein Lead-Formular im Rechner-Gewand ist:
          Roofora verdient nicht am Kostenrechner selbst, sondern erst dann, wenn Sie freiwillig und
          pro Handwerksbetrieb einzeln einer Kontaktfreigabe zustimmen. Deshalb brauchen Sie für den
          Kostenrechner weder Telefonnummer noch E-Mail-Adresse - auch der optionale Kundenaccount
          kommt nur mit Benutzername und Passwort aus. Ihre Daten werden dabei nie an Dritte
          verkauft: Einnahmen entstehen ausschließlich über die einzelne, von Ihnen selbst erteilte
          Freigabe, nicht über den pauschalen Weiterverkauf einer Anfrage.
        </p>
        <p>
          Die Durchschnittspreise sind dabei tagesaktuell: Jedes neue Handwerker-Angebot fließt
          sofort in die Berechnung ein, es gibt keine festen Update-Zyklen und keine veralteten
          Preislisten. Trotzdem bleibt der Durchschnittspreis eine Orientierung, kein verbindliches
          Angebot - die tatsächlichen Kosten für Ihr Dach hängen von individuellen Faktoren wie
          Dachzuschnitt, Speicherwahl und regionalen Handwerkerpreisen ab (mehr dazu im{' '}
          <Link to="/solaranlage-kosten">Kostenüberblick</Link>). Ein verlässlicher Festpreis
          entsteht erst aus einem echten Angebot für Ihr konkretes Projekt.
        </p>

        <p className="blog-post-cta seo-landing-cta">
          <button type="button" className="btn-primary" onClick={headerActions.onSelectCustomer}>
            Jetzt kostenlosen Auftrag erstellen
          </button>
        </p>

        <section className="faq seo-landing-faq">
          <h2 className="section-title">Häufige Fragen</h2>
          <div className="faq-list">
            {FAQ.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <nav className="seo-landing-related" aria-label="Weiterführende Themen">
          <h2>Weiterführende Themen</h2>
          <ul>
            <li>
              <Link to="/solaranlage-kosten">Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick →</Link>
            </li>
            <li>
              <Link to="/pv-angebot-vergleichen">So funktioniert der Angebotsvergleich →</Link>
            </li>
            <li>
              <Link to="/photovoltaik-planen">Photovoltaik Schritt für Schritt planen →</Link>
            </li>
            <li>
              <Link to="/pv-rechner">Passende Anlagengröße kostenlos berechnen →</Link>
            </li>
          </ul>
        </nav>
      </article>
      <MarketingFooter />
    </>
  )
}
